import type { FormEvent } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { queryKeys } from '@constants/queryKeys.constant';
import { satisfactionOptions } from '@constants/satisfactions.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatCentsToString } from '@formatters/currency.formatter';
import { formatDate } from '@formatters/date.formatter';
import { formatFullName } from '@formatters/fullName.formatter';
import { isAfterDate } from '@helpers/date/isAfter.helper';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import type { Intervention } from '@models/intervention.model';
import {
  updateInterventionSchema,
  type UpdateInterventionDto,
  type UpdateInterventionPayload,
} from '@services/interventions/dtos/update.dto';
import { interventionService } from '@services/interventions/service';
import type { UpdateInterventionOutput } from '@services/interventions/types/update.type';
import type { GetAllServiceRequestInterventionsOutput } from '@services/serviceRequests/types/getAllInterventions.type';

function interventionQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.interventions.detail(id),
    queryFn: () => interventionService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/interventions/$interventionId/update',
)({
  loader: ({ context, params }) => {
    const options = interventionQueryOptions(params.interventionId);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
  component: InterventionUpdate,
});

function InterventionUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id: serviceRequestId, interventionId } = Route.useParams();

  const navigate = Route.useNavigate();

  const { data } = useSuspenseQuery(interventionQueryOptions(interventionId));

  const form = useForm<UpdateInterventionDto>({
    resolver: zodResolver(updateInterventionSchema),
    mode: 'onBlur',
    defaultValues: {
      visitDate: formatDate(data.intervention.visitDate, {
        format: 'YYYY-MM-DD',
      }),
      workDate: formatDate(data.intervention.workDate, {
        format: 'YYYY-MM-DD',
      }),
      workEndDate: formatDate(data.intervention.workEndDate, {
        format: 'YYYY-MM-DD',
      }),
      remark: data.intervention.remark,
      invoiceAmount: formatCentsToString(data.intervention.invoiceAmountCents),
      quoteAmount: formatCentsToString(data.intervention.quoteAmountCents),
      satisfaction: data.intervention.satisfaction,
    },
  });

  const visitDate = useWatch({
    control: form.control,
    name: 'visitDate',
  });
  const workDate = useWatch({
    control: form.control,
    name: 'workDate',
  });

  const handleVisitDateChange = (event: FormEvent<HTMLInputElement>) => {
    const isWorkDateAfterVisit = isAfterDate(workDate, {
      dateToCompare: event.currentTarget.value,
    });

    if (!isWorkDateAfterVisit) {
      form.setValue('workDate', '', { shouldValidate: true });
      form.setValue('workEndDate', '', { shouldValidate: true });
    }
  };

  const { isPending, mutate: updateIntervention } = useMutation<
    UpdateInterventionOutput,
    unknown,
    UpdateInterventionPayload
  >({
    mutationFn: interventionService.update,
    onError: handleRequestError,
    onSuccess: async ({ intervention }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
      });

      queryClient.setQueriesData<
        InfiniteData<GetAllServiceRequestInterventionsOutput>
      >(
        {
          queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
        },
        oldData => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              list: page.list.map(item => {
                if (item.id === intervention.id) {
                  const updatedIntervention: Intervention = {
                    ...item,
                    visitDate: intervention.visitDate,
                    workDate: intervention.workDate,
                    workEndDate: intervention.workEndDate,
                    remark: intervention.remark,
                    satisfaction: intervention.satisfaction,
                    invoiceAmountCents: intervention.invoiceAmountCents,
                    quoteAmountCents: intervention.quoteAmountCents,
                  };
                  return updatedIntervention;
                }

                return item;
              }),
            })),
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: queryKeys.interventions.detail(interventionId),
      });

      invalidateSmartEventTimeline({
        id: serviceRequestId,
        queryClient,
        type: ESmartEventTimelineKeyType.ServiceRequest,
      });

      navigate({
        to: '/service-requests/$id',
        params: { id: serviceRequestId },
        replace: true,
      });
      toast.success('Success', {
        description: 'Intervention updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateIntervention({
      id: interventionId,
      remark: values.remark,
      invoiceAmount: values.invoiceAmount,
      quoteAmount: values.quoteAmount,
      satisfaction: values.satisfaction,
      visitDate:
        data.intervention.visitDate && !values.visitDate
          ? null
          : values.visitDate,
      workDate:
        data.intervention.workDate && !values.workDate ? null : values.workDate,
      workEndDate:
        data.intervention.workEndDate && !values.workEndDate
          ? null
          : values.workEndDate,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="Update intervention"
        to={'/service-requests/$id'}
      />

      <form className="space-y-2" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-x-2">
          <InputControlled
            defaultValue={data.intervention.service.translations.en}
            disabled
            label="Service"
            name="service"
          />

          <InputControlled
            defaultValue={
              data.intervention.provider
                ? data.intervention.provider.companyName ||
                  formatFullName({
                    firstName: data.intervention.provider.firstName,
                    lastName: data.intervention.provider.lastName,
                  })
                : 'No provider assigned'
            }
            disabled
            label="Provider"
            name="provider"
          />
        </div>

        <InputControlled
          label="Remark"
          maxLength={1000}
          name="remark"
          rows={8}
          showCount
          variant="textarea"
        />

        <Separator variant="form" />

        <div className="grid h-fit grid-cols-2 items-start gap-2">
          <InputControlled
            label="Visit date"
            name="visitDate"
            onChangeCapture={handleVisitDateChange}
            type="date"
            variant="base"
          />
          <InputControlled
            label="Quote amount"
            name="quoteAmount"
            prefix="€"
            variant="money"
          />
        </div>

        <Separator variant="form" />

        <div className="grid h-fit grid-cols-3 items-start gap-2">
          <InputControlled
            label="Work start date"
            min={formatDate(visitDate, {
              format: 'YYYY-MM-DD',
              daysToAdd: 1,
            })}
            name="workDate"
            type="date"
          />
          <InputControlled
            label="Work end date"
            min={formatDate(workDate || visitDate, { format: 'YYYY-MM-DD' })}
            name="workEndDate"
            type="date"
          />
          <InputControlled
            label="Invoice amount"
            name="invoiceAmount"
            prefix="€"
            variant="money"
          />
        </div>

        <Separator variant="form" />

        <InputControlled
          label="Satisfaction"
          name="satisfaction"
          options={satisfactionOptions}
          variant="select"
        />

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{ id: serviceRequestId }}
            rounded="lg"
            size="lg"
            to={'/service-requests/$id'}
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
